import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Filter, X } from 'lucide-react';
import { daysOf, spotCount } from './programAdminModel';
import { DayTabsBar } from './ProgramAdminHeader';
import ProgramList from './ProgramList';
import SidePanel from './SidePanel';
import {
  ActionBar, ActionBarButton, ActionBarClear, ActionBarCount, Column, Count, Toolbar, ToolbarNote, Toggle, Workspace,
} from './programAdminStyles';

// The Program area (plan §7.7 items 2 and 4): the day tabs, the timeline of the selected day, the
// "needs attention" filter and the "show removed" switch, the sticky bar for the ticked spots, and
// the side panel of whatever is selected. It holds no state of its own - every value and callback
// comes from the screen that owns the view state.
const ProgramArea = ({
  program, language, people = [], selection, dayId, checked = new Set(), needsAttentionOnly = false,
  showRemoved = false, attentionCount = 0, entryPosition,
  onSelectDay, onToggleChecked, onToggleNeedsAttention, onToggleShowRemoved, onSelectEntry, onSelectSpot,
  onClosePanel, onInviteSelected, onClearSelection, spotActions, entryActions, onChanged,
}) => {
  const intl = useIntl();
  const days = daysOf(program);
  const currentDay = days.find((d) => d.id === dayId) || days[0] || null;
  const totalSpots = spotCount(program);

  return (
    <Workspace $panel={!!selection}>
      <Column>
        <Toolbar>
          <Toggle $active={needsAttentionOnly}>
            <input type="checkbox" checked={needsAttentionOnly} onChange={(e) => onToggleNeedsAttention?.(e.target.checked)} />
            <Filter size={13} aria-hidden="true" />
            <FormattedMessage id="programAdmin.filter.attention" defaultMessage="Needs attention" />
            <Count $tone="attention">{attentionCount}</Count>
          </Toggle>
          <Toggle $active={showRemoved}>
            <input type="checkbox" checked={showRemoved} onChange={(e) => onToggleShowRemoved?.(e.target.checked)} />
            <FormattedMessage id="programAdmin.filter.showRemoved" defaultMessage="Show removed" />
          </Toggle>
          <ToolbarNote>
            <FormattedMessage id="programAdmin.filter.summary"
              defaultMessage="{count, plural, one {# spot} other {# spots}} on this event"
              values={{ count: totalSpots }} />
          </ToolbarNote>
        </Toolbar>

        <DayTabsBar days={days} activeId={currentDay?.id ?? null} language={language} onSelect={onSelectDay} />

        {currentDay && (
          <ProgramList
            program={program}
            language={language}
            dayId={currentDay.id}
            checked={checked}
            selection={selection}
            needsAttentionOnly={needsAttentionOnly}
            showRemoved={showRemoved}
            people={people}
            onSelectEntry={onSelectEntry}
            onSelectSpot={onSelectSpot}
            onToggleChecked={onToggleChecked}
            actions={spotActions}
          />
        )}

        {checked.size > 0 && (
          <ActionBar role="region" aria-label={intl.formatMessage({ id: 'programAdmin.selection.bar', defaultMessage: 'Selected spots' })}>
            <ActionBarCount>
              <FormattedMessage id="programAdmin.selection.count"
                defaultMessage="{count, plural, one {# spot selected} other {# spots selected}}"
                values={{ count: checked.size }} />
            </ActionBarCount>
            <ActionBarButton type="button" onClick={() => onInviteSelected?.([...checked])}>
              <FormattedMessage id="programAdmin.selection.invite" defaultMessage="Invite selected ({count})" values={{ count: checked.size }} />
            </ActionBarButton>
            <ActionBarClear type="button" onClick={onClearSelection}>
              <X size={13} aria-hidden="true" />
              <FormattedMessage id="programAdmin.selection.clear" defaultMessage="Clear" />
            </ActionBarClear>
          </ActionBar>
        )}
      </Column>

      <SidePanel
        program={program}
        language={language}
        selection={selection}
        people={people}
        entryPosition={entryPosition}
        spotActions={spotActions}
        entryActions={entryActions}
        onChanged={onChanged}
        onClose={onClosePanel}
      />
    </Workspace>
  );
};

export default ProgramArea;
